import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ApprovalCommonUsagePanel — §6.8 공통 모듈 시연 패널
 * C068 워크플로우·C066 상태 머신·C008 이벤트·C030 권한 등 재사용 현황 표시
 */
export function ApprovalCommonUsagePanel() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const usages = [
    {
      module: 'eraf-data-jpa (C068)',
      api: 'WorkflowService.start/approve/reject/listMyTasks',
      role: t('approval.commonUsage.col.role') + ': 워크플로우 인스턴스 생성·단계 전이·받은 결재 라우팅',
      snippet: 'workflowService.start(expenseDef, "approval:42", formData)',
    },
    {
      module: 'eraf-data-jpa (C068)',
      api: 'WorkflowDefinition.builder().step(name, role).build()',
      role: '결재선 정의 (@Bean) — 전이 엔진/Guard 자동 생성',
      snippet: 'WorkflowDefinition.builder("expense-approval").step("MANAGER_REVIEW","ROLE_MANAGER")...',
    },
    {
      module: 'eraf-core (C066)',
      api: 'StateMachine<WorkflowStepState, WorkflowEvent>',
      role: 'C068 이 내부 위임하는 전이 엔진 (본 biz 직접 호출 0줄)',
      snippet: '(C068 내부 — Guard: actorHasRole(approverRole))',
    },
    {
      module: 'eraf-core (C008)',
      api: 'EventPublisher.publish(BizEvent)',
      role: '결재 상신/결정 이벤트 발행 (notification-center 미래 구독)',
      snippet: 'eventPublisher.publish(new ApprovalDecidedEvent(id, APPROVED, actor))',
    },
    {
      module: 'eraf-core (C002)',
      api: 'ApiResponse.of / PageResponse',
      role: '표준 응답 래핑 · 목록 페이지 응답',
      snippet: 'return ApiResponse.page(service.search(cond, p))',
    },
    {
      module: 'eraf-core (C005)',
      api: 'JsonMapper.toJson/fromJson',
      role: '결재 본문(form_data_json) 직렬화·역직렬화',
      snippet: 'json.toJson(req.formData())',
    },
    {
      module: 'eraf-core (C014)',
      api: '@Audited(action="APPROVAL_REQUESTED")',
      role: '결재 상신 자기 감사 (전이 감사는 C068 자동)',
      snippet: '@Audited(action="APPROVAL_REQUESTED") public ApprovalRequestDetailResponse submit(...)',
    },
    {
      module: 'eraf-security (C030)',
      api: '@HasPermission("APPROVAL_DECIDE")',
      role: '승인/반려 메서드 단위 권한 (C066 Guard 와 이중 방어)',
      snippet: '@HasPermission("APPROVAL_DECIDE") public ... approve(id, comment)',
    },
    {
      module: 'eraf-data-jpa (C042)',
      api: 'GenericRepository.findAllActive(spec, pageable)',
      role: '운영 목록/내 결재 페이지 조회',
      snippet: 'repo.findAllActive(spec, pageable)',
    },
    {
      module: 'eraf-data-jpa (C041)',
      api: 'SpecificationBuilder + SortWhitelist',
      role: '동적 검색 + ORDER BY 인젝션 차단',
      snippet: 'SpecificationBuilder.of(...).eq("status", s).notDeleted().build()',
    },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        background: 'var(--color-surface-background)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--font-family-sans)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          textAlign: 'left',
        }}
      >
        <span>▾ {t('approval.commonUsage.title')}</span>
        <span style={{ fontSize: 'var(--font-size-xs)' }}>
          {t('approval.commonUsage.description')}
        </span>
      </button>

      {expanded && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {[
                  t('approval.commonUsage.col.module'),
                  t('approval.commonUsage.col.api'),
                  t('approval.commonUsage.col.role'),
                  t('approval.commonUsage.col.snippet'),
                ].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usages.map((row, i) => (
                <tr
                  key={i}
                  style={{
                    background: i % 2 === 0 ? 'var(--color-surface-background)' : 'var(--color-surface-muted)',
                  }}
                >
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontWeight: 600,
                      color: 'var(--color-brand-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.api}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      minWidth: 200,
                    }}
                  >
                    {row.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--font-size-xs)',
                      maxWidth: 320,
                      overflowWrap: 'break-word',
                    }}
                  >
                    {row.snippet}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
